import { ReactNode } from 'react';
import { Sidebar, SidebarProps } from './Sidebar';
import { TopBar, TopBarProps } from './TopBar';

/**
 * AppLayout — the standard authenticated-app frame.
 *
 * Composed pattern (not a single M3 component) per Agent Protocol step 2:
 *   - M3 lays out apps as Navigation Drawer + Top App Bar + content
 *     region (https://m3.material.io/foundations/layout/canonical-layouts/overview)
 *   - Populus follows the canonical pattern with CSS Grid (vs M3's
 *     implied flexbox)
 *
 * Populus adaptations applied:
 *   - CSS Grid template areas: 'sidebar topbar' / 'sidebar content'
 *     (vs M3 row+column flex; grid is cleaner for the persistent
 *     two-axis layout)
 *   - Sidebar persistent on desktop ≥1024px; below that it collapses
 *     (deferred — `display: none` for now until a mobile cycle ships)
 *   - Content region scrolls independently of the Sidebar (sticky)
 *     and TopBar (sticky) — operator can scroll a long list without
 *     losing nav or breadcrumbs
 *   - Max content width 1280px (M3 implies; Populus locks for
 *     scanability — wider rows become harder to read)
 *
 * Token discipline:
 *   - Content background → --color-bg (system; warm off-white)
 *   - Sidebar surface (in child component) → --color-sidebar (system)
 *   - TopBar surface (in child component) → --color-card (system)
 *
 * Phase 11 Block 6 PR 4 (2026-05-12).
 */

export interface AppLayoutProps {
  sidebar: SidebarProps;
  topbar?: TopBarProps;
  children: ReactNode;
  contentMaxWidth?: number;
}

export function AppLayout({
  sidebar,
  topbar,
  children,
  contentMaxWidth = 1280,
}: AppLayoutProps) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateAreas: '"sidebar topbar" "sidebar content"',
      gridTemplateColumns: '240px 1fr',
      gridTemplateRows: '56px 1fr',
      minHeight: '100vh',
      background: 'var(--color-bg)',
    }}>
      <Sidebar {...sidebar} />
      {topbar && <TopBar {...topbar} />}
      <main style={{
        gridArea: 'content',
        padding: 'var(--sp-6) var(--sp-7)',
        overflowX: 'auto',
      }}>
        <div style={{ maxWidth: contentMaxWidth, margin: '0 auto' }}>
          {children}
        </div>
      </main>
    </div>
  );
}
